export type FinancialItemType = 'asset' | 'liability';

export type FinancialRating = 'excellent' | 'good' | 'neutral' | 'bad' | 'critical';

export type AssetDomain = 'personal' | 'business';

export type AssetCategory =
  | 'real_estate'
  | 'cash'
  | 'securities'
  | 'private_equity'
  | 'collectibles'
  | 'vehicle'
  | 'business_equity'
  | 'other_asset'
  | 'mortgage'
  | 'loan'
  | 'credit_line'
  | 'tax'
  | 'other_liability';

export type FinancialItem = {
  id: string;
  name: string;
  type: FinancialItemType;
  /** Defaults to `personal` for legacy docs without a domain. */
  domain?: AssetDomain;
  /** Structured category; legacy free-text strings are normalized on read/write. */
  category: AssetCategory | string;
  currentValue: number;
  monthlyChange: number;
  interestRate?: number;
  rating: FinancialRating;
  acquisitionDate?: string;
  notes?: string;
  /** Liabilities: optional link to an asset id (e.g. mortgage → property). */
  linkedAssetId?: string;
  /** Assets: optional manual appreciation % p.a. for projections. */
  appreciationRate?: number;
  /** Liabilities: minimum monthly payment (€). */
  minimumPayment?: number;
  createdAt: string;
  updatedAt: string;
};

export type ValuationMethod = 'manual' | 'revenueMultiple' | 'ebitdaMultiple';

export type InvestmentStatus = 'active' | 'exit' | 'dead';

export type InvestmentCaseFounder = {
  id: string;
  name?: string;
  linkedinUrl?: string;
  profileText?: string;
};

export type ClaimEvidence = 'supported' | 'weak' | 'likely_false' | 'speculative';

export type DeckIntelligenceSectionScores = {
  team: number;
  market: number;
  product: number;
  businessModel: number;
  traction: number;
  funding: number;
};

export type DeckIntelligenceCompetitor = {
  name: string;
  websiteUrl?: string;
  marketStrength: number;
  note?: string;
};

export type DeckIntelligence = {
  /** ISO timestamp when analysis finished */
  analyzedAt: string;
  /** Hash or prefix of deckOcrText used — detect stale analysis */
  sourceTextFingerprint: string;
  model: string;

  team: string;
  market: string;
  product: string;
  businessModel: string;
  traction: string;
  funding: string;
  competition: string;
  sectionScores?: DeckIntelligenceSectionScores;
  competitors?: DeckIntelligenceCompetitor[];

  risks: Array<{ description: string; riskEvaluation: string }>;
  claims: Array<{ name: string; evidenceEvaluation: ClaimEvidence }>;
  missing: string[];

  inversePitch: {
    whyFailure: string;
    failureReason: string;
    whySuccess: string;
    successReason: string;
  };

  investorMemo: {
    bullCase: string;
    bearCase: string;
    devilsAdvocate: string;
    contrarianOpportunity: string;
  };

  analysis: {
    top3UnicornReasons: string[];
    top3DeathReasons: string[];
    mostDangerousAssumption: string;
    hundredXRequirements: string;
    doubleDownEvidence: string;
  };

  historicallySimilarCompanies: Array<{ name: string; similarity: string }>;
};

/**
 * Pipeline / DD view of a potential investment (Firestore `investmentCases`).
 * Company-facing metadata mirrors investments; scores are subjective 1–6.
 */
export type InvestmentCase = {
  id: string;
  name: string;
  websiteUrl?: string;
  countryCode?: string;
  foundingYear?: number;
  industry: string;
  founderAmbition: number;
  timeToRevenue: number;
  exitExperience: number;
  founderSkillset: number;
  marketSize: number;
  /** Pitch deck PDF in Firebase Storage */
  deckPdfUrl?: string;
  deckPdfStoragePath?: string;
  deckPdfFileName?: string;
  /** OCR text from the deck — paste manually or fill via cloud function later */
  deckOcrText?: string;
  /** Structured DD brief from deck analysis (OpenAI) */
  deckIntelligence?: DeckIntelligence;
  founders?: InvestmentCaseFounder[];
  createdAt: string;
  updatedAt: string;
};

export type BusinessOpportunityStatus = 'draft' | 'refining' | 'validated' | 'archived';

export type OpportunityRefinement = {
  id: string;
  createdAt: string;
  type: 'user' | 'ai';
  content: string;
};

export type OpportunityIterationTrigger = 'analyze' | 'refine' | 'resolution';

/** Lightweight score snapshot taken each time a new analysis lands. */
export type OpportunityIteration = {
  id: string;
  createdAt: string;
  trigger: OpportunityIterationTrigger;
  /** What changed in this iteration. */
  summary: string;
  /** Overall opportunity score (0–10) at this point. */
  overallScore: number;
  sectionScores?: DeckIntelligenceSectionScores;
};

export type OpportunityFeedbackSentiment = 'positive' | 'neutral' | 'negative';

export type OpportunityFeedback = {
  id: string;
  createdAt: string;
  source: string;
  quote: string;
  sentiment?: OpportunityFeedbackSentiment;
};

export type OpportunityPnlMonth = {
  /** Month within the plan year (1–12). */
  month: number;
  revenue: number;
  /** Other operating income (grants, sublets). Missing on older docs. */
  otherIncome?: number;
  cogs: number;
  personnel: number;
  /** Rent, utilities, coworking. Missing on older docs. */
  rentPremises?: number;
  marketing: number;
  researchDevelopment: number;
  /** Vehicle & travel costs. Missing on older docs. */
  vehicleTravel?: number;
  /** Insurance, chamber & association fees. Missing on older docs. */
  insuranceContributions?: number;
  /** Repairs & maintenance. Missing on older docs. */
  repairsMaintenance?: number;
  generalAdmin: number;
  otherOpex: number;
  depreciation: number;
  interestExpense: number;
  taxes: number;
};

export type OpportunityPnlYearPlan = {
  /** Plan year index (1, 2, or 3). */
  year: number;
  months: OpportunityPnlMonth[];
};

/** @deprecated Legacy annual rollup — use yearPlans. */
export type OpportunityPnlYear = {
  year: number;
  revenue: number;
  cogs: number;
  grossProfit: number;
  opex: number;
  ebitda: number;
};

export type OpportunityPnl = {
  generatedAt: string;
  model: string;
  currency: 'EUR';
  summary: string;
  /** Month-by-month P&L for years 1–3. */
  yearPlans: OpportunityPnlYearPlan[];
  /** @deprecated Legacy annual rows kept for older docs. */
  years?: OpportunityPnlYear[];
  breakEvenMonth?: number;
  keyAssumptions: string[];
  revenueDrivers: string[];
  costDrivers: string[];
};

export type OpportunityRoadmapPhase = 'now' | 'next' | 'later';

export type OpportunityRoadmapLevel = 'high' | 'medium' | 'low';

export type OpportunityRoadmapItem = {
  id: string;
  phase: OpportunityRoadmapPhase;
  title: string;
  description: string;
  impact: OpportunityRoadmapLevel;
  effort: OpportunityRoadmapLevel;
  /** Concise timeframe, e.g. "Weeks 1-3" or "Month 3". */
  timeframe?: string;
  done?: boolean;
};

/** AI-generated initial feature roadmap, editable per opportunity. */
export type OpportunityRoadmap = {
  generatedAt: string;
  model: string;
  summary: string;
  items: OpportunityRoadmapItem[];
  /** Last manual edit, if any. */
  editedAt?: string;
};

/** Pre-dealflow idea in the idea refinery (Firestore `businessOpportunities`). */
export type BusinessOpportunity = {
  id: string;
  name: string;
  industry?: string;
  ideaSummary: string;
  status: BusinessOpportunityStatus;
  ideaIntelligence?: DeckIntelligence;
  refinements?: OpportunityRefinement[];
  iterations?: OpportunityIteration[];
  feedback?: OpportunityFeedback[];
  pnl?: OpportunityPnl;
  roadmap?: OpportunityRoadmap;
  promotedCaseId?: string;
  createdAt: string;
  updatedAt: string;
};

/** Persisted chat exchange scoped to one business opportunity (Firestore `opportunityChatMessages`). */
export type OpportunityChatMessage = {
  id: string;
  userId: string;
  opportunityId: string;
  /** Absent on messages written before opportunity chats had threads. */
  threadId?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

/**
 * Chat thread scoped to one business opportunity (Firestore `opportunityChatThreads`).
 * Document id `{userId}__{id}`.
 */
export type OpportunityChatThread = {
  /** Thread id — must not contain `__`. */
  id: string;
  userId: string;
  opportunityId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

/** Persisted chat exchange scoped to one portfolio company (Firestore `investmentChatMessages`). */
export type InvestmentChatMessage = {
  id: string;
  userId: string;
  companyId: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

/**
 * Chat thread scoped to one portfolio company (Firestore `investmentChatThreads`).
 * Document id `{userId}__{id}`.
 */
export type InvestmentChatThread = {
  /** Thread id — must not contain `__`. */
  id: string;
  userId: string;
  companyId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

/** Persisted chat exchange scoped to one dealflow investment case (Firestore `investmentCaseChatMessages`). */
export type InvestmentCaseChatMessage = {
  id: string;
  userId: string;
  caseId: string;
  /** Absent on messages written before investment case chats had threads. */
  threadId?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

/**
 * Chat thread scoped to one dealflow investment case (Firestore `investmentCaseChatThreads`).
 * Document id `{userId}__{id}`.
 */
export type InvestmentCaseChatThread = {
  /** Thread id — must not contain `__`. */
  id: string;
  userId: string;
  caseId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

/** Persisted chat exchange scoped to one real-estate property (Firestore `realEstateChatMessages`). */
export type RealEstateChatMessage = {
  id: string;
  userId: string;
  propertyId: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

/**
 * Chat thread scoped to one real-estate property (Firestore `realEstateChatThreads`).
 * Document id `{userId}__{id}`.
 */
export type RealEstateChatThread = {
  /** Thread id — must not contain `__`. */
  id: string;
  userId: string;
  propertyId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

export type InvestmentCompany = {
  id: string;
  name: string;
  /** Company website — used for favicon (Google s2 service) and links. */
  websiteUrl?: string;
  /** ISO 3166-1 alpha-2 (e.g. de, us); shown as flag in lists. */
  countryCode?: string;
  foundingYear?: number;
  industry: string;
  ownershipPercentage: number;
  /** Investment ticket / round size (€). */
  ticketSize: number;
  /**
   * ISO timestamp of when the investment was made.
   * Used as the start of the holding period.
   * Empty string for legacy records without a recorded date.
   */
  investmentDate: string;
  /**
   * Holding lifecycle. Omitted in older records — treat as `active` in UI.
   */
  investmentStatus?: InvestmentStatus;
  /**
   * When `investmentStatus` is `exit` or `dead`, ISO timestamp for exit / write-off.
   */
  statusDate?: string;
  /**
   * Total cash proceeds (€) on exit. Meaningful when `investmentStatus` is `exit`.
   */
  exitTotalEuros?: number;
  valuationMethod: ValuationMethod;
  manualValuation?: number;
  revenueMultiple?: number;
  ebitdaMultiple?: number;
  exitProbabilityThisYear: number;
  expectedDividendThisYear: number;
  /**
   * Years until expected return event (exit or liquidity). Used in future returns projections.
   */
  expectedReturnLeadTime?: number;
  /**
   * Expected return multiple on ticket size (e.g. 2.5 for 2.5x, 10 for 10x).
   * Used in future returns projections.
   */
  expectedReturnMultiple?: number;
  createdAt: string;
  updatedAt: string;
};

export type UpdateSentiment = 'positive' | 'neutral' | 'negative' | 'mixed';

export type FounderUpdateSource = 'manual' | 'email';

export type FounderUpdateExtraction = {
  summary: string;
  sentiment: UpdateSentiment;
  keyPoints: string[];
  asks: string[];
  risks: string[];
  milestones: string[];
  periodMonth?: string;
  kpis: {
    revenue?: number;
    burn?: number;
    remainingCash?: number;
    runwayMonths?: number;
    growthRate?: number;
    customerCount?: number;
  };
  followOnSignal?: 'none' | 'watch' | 'consider';
  confidence: 'low' | 'medium' | 'high';
  unmatchedCompanyCandidates?: string[];
};

export type CompanyUpdate = {
  id: string;
  companyId: string;
  date: string;
  rawText: string;
  aiSummary: string;
  sentiment: UpdateSentiment;
  keyPoints: string[];
  /** `manual` when entered in-app; `email` when created from forwarded founder mail. */
  source?: FounderUpdateSource;
  sourceInboxItemId?: string;
  sourceMessageId?: string;
  fromEmail?: string;
  subject?: string;
  receivedAt?: string;
  extraction?: FounderUpdateExtraction;
  createdAt: string;
};

export type FounderUpdateInboxItemStatus = 'unmatched' | 'matched' | 'processed' | 'dismissed';

const INBOX_ITEM_ID_SAFE = /^[a-zA-Z0-9._-]+$/;

/**
 * Stable inbox item id from provider message id (no `__`). Used for idempotent inbound writes.
 */
export function founderUpdateInboxItemIdFromSourceMessageId(sourceMessageId: string): string {
  const normalized = sourceMessageId.trim();
  if (!normalized) {
    throw new Error('sourceMessageId is required');
  }
  if (!normalized.includes('__') && normalized.length <= 128 && INBOX_ITEM_ID_SAFE.test(normalized)) {
    return normalized;
  }
  let hash = 2166136261;
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `msg-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

/**
 * Raw inbound founder email before or after company matching (Firestore `founderUpdateInboxItems`).
 * Document id `{userId}__{id}` — written by backend on inbound webhook; clients read/delete own items.
 */
export type FounderUpdateInboxItem = {
  /** Inbox item id — must not contain `__`. */
  id: string;
  userId: string;
  status: FounderUpdateInboxItemStatus;
  companyId?: string;
  sourceMessageId: string;
  fromEmail: string;
  subject?: string;
  receivedAt: string;
  rawText: string;
  extraction?: FounderUpdateExtraction;
  createdUpdateId?: string;
  createdAt: string;
  updatedAt: string;
};

export type PortfolioRadarSignalType =
  | 'missing_update'
  | 'runway_risk'
  | 'negative_trend'
  | 'follow_on_candidate'
  | 'founder_ask';

export type PortfolioRadarSignalSeverity = 'info' | 'watch' | 'urgent';

/**
 * Computed portfolio attention signal (Firestore `portfolioRadarSignals`).
 * Document id `{userId}__{id}` — written by backend; clients read/delete own signals.
 */
export type PortfolioRadarSignal = {
  /** Signal id — must not contain `__`. */
  id: string;
  userId: string;
  companyId: string;
  type: PortfolioRadarSignalType;
  severity: PortfolioRadarSignalSeverity;
  title: string;
  reason: string;
  sourceUpdateIds: string[];
  route: string;
  createdAt: string;
  updatedAt: string;
};

export type FounderUpdateMetricExtraction = {
  revenue?: number;
  burn?: number;
  remainingCash?: number;
  runwayMonths?: number;
  headcount?: number;
  confidence: 'high' | 'medium' | 'low';
  notes: string;
};

export type FounderUpdateAsk = {
  type: 'intro' | 'hiring' | 'fundraising' | 'customer' | 'advice' | 'other';
  description: string;
  urgency: 'low' | 'medium' | 'high';
};

export type FounderUpdateIntelligence = {
  /** ISO timestamp when analysis finished */
  analyzedAt: string;
  /** Hash of source text used — detect stale analysis */
  sourceTextFingerprint: string;
  model: string;
  summary: string;
  sentiment: UpdateSentiment;
  healthSignal: 'improving' | 'stable' | 'deteriorating' | 'unclear';
  extractedMetrics: FounderUpdateMetricExtraction;
  whatChanged: string[];
  milestones: string[];
  risks: Array<{ description: string; severity: 'low' | 'medium' | 'high'; rationale: string }>;
  asks: FounderUpdateAsk[];
  followUpQuestions: string[];
  investorNotes: string;
};

/**
 * Lean investor-style monthly report per company (Firestore `monthlyUpdates`).
 * All business fields optional; `id`, `companyId`, timestamps are always set in-app.
 */
export type MonthlyUpdate = {
  id: string;
  companyId: string;
  /** Report month, e.g. first day of month as ISO date `2024-06-01` */
  periodMonth?: string;
  /** Monthly revenue (€), if disclosed */
  revenue?: number;
  /** Monthly burn / spend (€), if disclosed */
  burn?: number;
  /** Cash position at end of period (€), if disclosed. Runway is derived from this and `burn`. */
  remainingCash?: number;
  /** Subjective sentiment of the founder/report tone, 1 (very negative) – 6 (very positive). */
  reportSentiment?: number;
  /** Subjective exit-probability rating, 1 (very unlikely) – 6 (very likely). */
  exitProbability?: number;
  /** Subjective overall company health, 1 (critical) – 6 (excellent). */
  companyHealth?: number;
  /** Pasted deck, email thread, or long notes */
  rawText?: string;
  /** After PDF upload to Storage */
  pdfUrl?: string;
  /** Storage path for replace/delete */
  pdfStoragePath?: string;
  originalPdfFileName?: string;
  /** Structured investor briefing from founder update analysis (OpenAI) */
  updateIntelligence?: FounderUpdateIntelligence;
  createdAt: string;
  updatedAt: string;
};

export type BankProvider = 'qonto' | 'manual';

/** Whether the account holds cash (account) or securities (depot). */
export type BankAccountKind = 'account' | 'depot';

/** ISO 4217 currency codes offered for manual accounts. */
export type CurrencyCode =
  | 'EUR'
  | 'USD'
  | 'GBP'
  | 'CHF'
  | 'JPY'
  | 'AUD'
  | 'CAD'
  | 'SEK'
  | 'NOK'
  | 'DKK'
  | 'PLN'
  | 'CZK';

export type BankConnectionStatus = 'pending' | 'connected' | 'error' | 'disconnected';

/** Qonto (or future) OAuth connection — tokens written by backend only. */
export type BankConnection = {
  id: string;
  userId: string;
  provider: BankProvider;
  status: BankConnectionStatus;
  organizationName?: string;
  lastSyncAt?: string;
  lastSyncError?: string;
  createdAt: string;
  updatedAt: string;
};

export type BankAccount = {
  id: string;
  userId: string;
  connectionId: string;
  provider: BankProvider;
  /** Provider account id (e.g. Qonto account id). */
  externalId: string;
  ibanMasked: string;
  /** Free-form account/depot number for manual accounts. */
  accountNumber?: string;
  name: string;
  /** Cash account vs securities depot. Defaults to 'account' when absent (legacy/synced). */
  kind?: BankAccountKind;
  /** Bank website — used for favicon in the list and detail header. */
  websiteUrl?: string;
  currency: CurrencyCode;
  balance: number;
  balanceUpdatedAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type BankTransaction = {
  id: string;
  userId: string;
  bankAccountId: string;
  externalId: string;
  /** ISO timestamp when the transaction was booked. */
  bookedAt: string;
  /** Signed amount in € (outflows negative). */
  amount: number;
  currency: 'EUR';
  counterpartyName: string;
  label: string;
  reference?: string;
  /** User override category (e.g. asset registry category or transfer/fees). */
  category?: string;
  createdAt: string;
};

/** Kind of real-estate holding, used for grouping and labels. */
export type RealEstatePropertyType =
  | 'apartment'
  | 'house'
  | 'multi_family'
  | 'commercial'
  | 'land'
  | 'vacation'
  | 'other';

/** Status of a project phase in a real estate construction/renovation project. */
export type ProjectPhaseStatus = 'pending' | 'in_progress' | 'completed' | 'blocked';

/**
 * A task within a real-estate project phase. Tasks can optionally sync to the
 * global to-do list via `addToGlobalTasks`.
 */
export type RealEstateProjectTask = {
  id: string;
  phaseId: string;
  title: string;
  done: boolean;
  dueDate?: string;
  assignee?: string;
  notes?: string;
  /** If true, this task also appears in the global task list. */
  addToGlobalTasks?: boolean;
  createdAt: string;
  completedAt: string | null;
};

/**
 * A phase in a real-estate construction/renovation project, displayed in a
 * Gantt-like timeline view. Phases can have dependencies on other phases.
 */
export type RealEstateProjectPhase = {
  id: string;
  name: string;
  status: ProjectPhaseStatus;
  /** Dependencies — array of phase IDs that must be completed before this phase can start. */
  dependsOn?: string[];
  /** Expected start date (ISO YYYY-MM-DD). */
  expectedStartDate?: string;
  /** Expected end/completion date (ISO YYYY-MM-DD). */
  expectedEndDate?: string;
  /** Actual completion date when status is completed (ISO YYYY-MM-DD). */
  completedDate?: string;
  /** Responsible party or contact for this phase. */
  responsibleParty?: string;
  notes?: string;
  /** Sort order for display; lower numbers appear first. */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * A real-estate holding (Firestore `realEstateItems`).
 * Document id `{userId}__{id}`. Property value counts as an asset and the
 * outstanding mortgage as a liability; net equity flows into net worth.
 */
export type RealEstateProperty = {
  /** Property id — must not contain `__`. */
  id: string;
  userId: string;
  name: string;
  propertyType: RealEstatePropertyType;
  /** Free-form street address / location. */
  address?: string;
  /** Listing or management website — used for the favicon shown in lists. */
  websiteUrl?: string;
  /** Current market value (€) — counts as a gross asset. */
  currentValue: number;
  /** Original purchase price (€). */
  purchasePrice?: number;
  /** ISO calendar date of acquisition `YYYY-MM-DD`. */
  purchaseDate?: string;
  /** Outstanding mortgage / loan balance (€) — counts as a liability. */
  mortgageBalance?: number;
  /** Gross monthly rental income (€). */
  monthlyRent?: number;
  /** Optional manual appreciation % p.a. for projections. */
  appreciationRate?: number;
  notes?: string;
  /** When the market value was last updated. */
  valueUpdatedAt?: string;
  /** Project phases for construction/renovation projects. */
  projectPhases?: RealEstateProjectPhase[];
  /** Tasks associated with project phases. */
  projectTasks?: RealEstateProjectTask[];
  createdAt: string;
  updatedAt: string;
};

/** German legal forms for group entities (ported from n27). */
export type LegalForm =
  | 'GmbH'
  | 'GmbH & Co. KG'
  | 'AG'
  | 'UG (haftungsbeschränkt)'
  | 'KG'
  | 'OHG'
  | 'e.K.'
  | 'Sonstige';

export const LEGAL_FORMS: LegalForm[] = [
  'GmbH',
  'GmbH & Co. KG',
  'AG',
  'UG (haftungsbeschränkt)',
  'KG',
  'OHG',
  'e.K.',
  'Sonstige',
];

/**
 * A legal entity in the user's group structure (Firestore `legalEntities`).
 * Document id `{userId}__{id}`. Entities form a forest via `parentEntityId`;
 * archived entities stay in the collection but are hidden from views.
 */
export type LegalEntity = {
  /** Entity id — must not contain `__`. */
  id: string;
  userId: string;
  name: string;
  legalForm: LegalForm;
  /** Registered seat (Sitz), e.g. "München". */
  seat?: string;
  /** Parent entity in the group structure; null = group root candidate. */
  parentEntityId?: string | null;
  /** Ownership share the parent holds, in basis points (10000 = 100 %). */
  ownershipBps?: number | null;
  /** ISO timestamp when archived; null/absent = active. Archive instead of delete. */
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Per-domain or combined net-worth figures on a stored snapshot. */
export type NetWorthSnapshotTotals = {
  assets: number;
  liabilities: number;
  investments: number;
  bankCash: number;
  /** Net real-estate equity (market value − outstanding mortgage). */
  realEstate: number;
  netWorth: number;
};

/**
 * Combined gross-asset allocation by category for a stored snapshot.
 * Keys are registry asset categories plus `portfolio` and `bank_cash`.
 */
export type NetWorthAllocationBreakdown = Record<string, number>;

/**
 * Daily net-worth snapshot (Firestore `netWorthSnapshots`).
 * Document id `{userId}__{id}` where `id` is `snapshotDate` (`YYYY-MM-DD`).
 */
export type NetWorthSnapshot = {
  /** Calendar day id, e.g. `2026-06-17` — must not contain `__`. */
  id: string;
  userId: string;
  /** ISO calendar date `YYYY-MM-DD` for this snapshot. */
  snapshotDate: string;
  personal: NetWorthSnapshotTotals;
  business: NetWorthSnapshotTotals;
  combined: NetWorthSnapshotTotals;
  /** Combined gross-asset allocation by category; absent on legacy snapshots. */
  allocation?: NetWorthAllocationBreakdown;
  createdAt: string;
};

/** Parent entity kind for an uploaded document attachment. */
export type EntityDocumentEntityType = 'investment' | 'real_estate' | 'financial_item';

/**
 * User-uploaded file attached to an investment, real-estate property, or registry item.
 * Firestore `entityDocuments` — document id `{userId}__{id}`.
 */
export type EntityDocument = {
  /** Document id — must not contain `__`. */
  id: string;
  userId: string;
  entityType: EntityDocumentEntityType;
  entityId: string;
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  contentType: string;
  sizeBytes: number;
  /** Plain-text extracted on upload for search and RAG indexing. */
  extractedText?: string;
  notes?: string;
  uploadedAt: string;
  updatedAt: string;
};

/** Source document kind for a RAG embedding chunk. */
export type RagSourceType =
  | 'financial_item'
  | 'investment'
  | 'bank_account'
  | 'bank_transaction'
  | 'company_update'
  | 'monthly_update'
  | 'investment_case'
  | 'real_estate'
  | 'business_opportunity';

/**
 * Embedded text chunk for RAG search (Firestore `ragChunks`).
 * Document id `{userId}__{id}` — written by backend only; clients read own chunks.
 */
export type RagChunk = {
  /** Chunk id — must not contain `__`. */
  id: string;
  userId: string;
  sourceType: RagSourceType;
  sourceId: string;
  text: string;
  /** `text-embedding-3-small` vector (1536 dimensions). */
  embedding: number[];
  contentFingerprint: string;
  updatedAt: string;
};

export type ChatRole = 'user' | 'assistant' | 'system';

/** RAG source reference attached to an assistant message. */
export type ChatCitation = {
  sourceType: RagSourceType;
  sourceId: string;
  excerpt: string;
};

/**
 * Intelligence chat thread (Firestore `chatThreads`).
 * Document id `{userId}__{id}`.
 */
export type ChatThread = {
  /** Thread id — must not contain `__`. */
  id: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Message in an Intelligence chat thread (Firestore `chatMessages`).
 * Document id `{userId}__{id}`; each doc must stay under 32 KB.
 */
export type ChatMessage = {
  /** Message id — must not contain `__`. */
  id: string;
  userId: string;
  threadId: string;
  role: ChatRole;
  content: string;
  citations?: ChatCitation[];
  createdAt: string;
};

export type ScenarioLeverCategory =
  | 'investment'
  | 'savings'
  | 'debt'
  | 'real_estate'
  | 'income'
  | 'other';

export type ScenarioLeverConfidence = 'high' | 'medium' | 'low';

/** Action lever in a goal-based net-worth scenario. */
export type ScenarioLever = {
  id: string;
  title: string;
  description: string;
  category: ScenarioLeverCategory;
  impactEuros?: number;
  confidence: ScenarioLeverConfidence;
};

export type NetWorthScenarioYearlyPoint = {
  year: number;
  netWorth: number;
};

/**
 * Saved net-worth target scenario (Firestore `netWorthScenarios`).
 * Document id `{userId}__{id}`.
 */
export type NetWorthScenario = {
  /** Scenario id — must not contain `__`. */
  id: string;
  userId: string;
  targetNetWorth: number;
  horizonYears: number;
  baselineNetWorth: number;
  projectedNetWorth: number;
  summary: string;
  levers: ScenarioLever[];
  yearlyProjection: NetWorthScenarioYearlyPoint[];
  assumptions: string[];
  createdAt: string;
};

export type ScenarioActionType = 'payOff' | 'sell' | 'buy' | 'hold' | 'improve' | 'avoid';

export type ScenarioPriority = 'low' | 'medium' | 'high';

export type ScenarioAction = {
  title: string;
  description: string;
  type: ScenarioActionType;
  priority: ScenarioPriority;
  affectedItemName?: string;
  amount?: number;
};

export type FinancialScenarioResult = {
  summary: string;
  recommendedActions: ScenarioAction[];
};

/**
 * Personal to-do item in the simple task tracker. The whole list is stored as
 * an array field (`tasks`) on the user doc (`users/{uid}`); array order is the
 * user-chosen sort order.
 */
export type TaskItem = {
  id: string;
  title: string;
  done: boolean;
  createdAt: string;
  completedAt: string | null;
};
